import { useState } from "react";
import { Box, Typography, Avatar, Chip, Button } from "@mui/material";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import PeopleOutlineIcon from "@mui/icons-material/PeopleOutline";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import TableRestaurantOutlinedIcon from "@mui/icons-material/TableRestaurantOutlined";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import dayjs from "dayjs";
import { Booking, BookingStatus } from "src/business/booking";
import { buildWhatsappUrl } from "src/business/whatsapp";
import {
  BookingEventType,
  getAllowedBookingTransitions,
} from "src/business/bookingMachine";
import { BookingStatusBadge } from "./BookingStatusBadge";
import { BookingDetailsModal } from "./BookingDetailsModal";
import { NoShowWarningIndicator } from "./NoShowWarningIndicator";
import {
  colorBorder,
  colorTextPrimary,
  colorTextMuted,
  colorSurfaceHover,
  colorTextDisabled,
  colorChipBackground,
  colorTextSecondary,
  colorDealBg,
  colorDealBorder,
  colorDealText,
  colorDealAccent,
  colorBorderSuccessEmphasis,
  iconColorSuccess,
  colorSurfaceSuccess,
  iconBgSuccess,
  colorBorderError,
  colorActionDanger,
  colorSurfaceError,
  colorSurfaceDangerHover,
  iconBgInfo,
  iconColorInfo,
  colorBorderInfo,
  iconBgPurple,
  iconColorPurple,
  iconBgPink,
  iconColorPink,
} from "src/design/tokens";
import { useTranslation } from "react-i18next";

// Visual style + label per transition event (not target): CHECK_IN and
// CORRECT_TO_CHECKED_IN share the CHECKED_IN target but read differently, so the
// event is the right key. Driving both off one map keeps the action buttons
// consistent with the status badge colors.
const EVENT_ACTIONS: Record<
  BookingEventType,
  { labelKey: string; bg: string; color: string; border: string; hoverBg: string; hoverBorder: string }
> = {
  CONFIRM: {
    labelKey: "bookings.status.actions.confirm",
    bg: colorSurfaceSuccess, color: iconColorSuccess, border: colorBorderSuccessEmphasis,
    hoverBg: iconBgSuccess, hoverBorder: iconColorSuccess,
  },
  CANCEL: {
    labelKey: "bookings.status.actions.cancel",
    bg: colorSurfaceError, color: colorActionDanger, border: colorBorderError,
    hoverBg: colorSurfaceDangerHover, hoverBorder: colorActionDanger,
  },
  CHECK_IN: {
    labelKey: "bookings.status.actions.checkIn",
    bg: iconBgInfo, color: iconColorInfo, border: colorBorderInfo,
    hoverBg: iconBgInfo, hoverBorder: iconColorInfo,
  },
  CORRECT_TO_CHECKED_IN: {
    labelKey: "bookings.status.actions.correctToCheckedIn",
    bg: iconBgInfo, color: iconColorInfo, border: colorBorderInfo,
    hoverBg: iconBgInfo, hoverBorder: iconColorInfo,
  },
  NO_SHOW: {
    labelKey: "bookings.status.actions.noShow",
    bg: colorSurfaceError, color: colorActionDanger, border: colorBorderError,
    hoverBg: colorSurfaceDangerHover, hoverBorder: colorActionDanger,
  },
};

// WhatsApp notification offered per booking status: a confirmation on
// CONFIRMED bookings, a cancellation notice on CANCELED ones. Statuses absent
// from this map get no WhatsApp control. Colors mirror the matching status
// action button so the notice reads as the follow-up to that transition.
const WHATSAPP_ACTIONS: Partial<
  Record<
    BookingStatus,
    {
      labelKey: string;
      messageKey: string;
      bg: string;
      color: string;
      border: string;
      hoverBg: string;
      hoverBorder: string;
    }
  >
> = {
  [BookingStatus.CONFIRMED]: {
    labelKey: "bookings.whatsapp.confirmLabel",
    messageKey: "bookings.whatsapp.confirmMessage",
    bg: colorSurfaceSuccess, color: iconColorSuccess, border: colorBorderSuccessEmphasis,
    hoverBg: iconBgSuccess, hoverBorder: iconColorSuccess,
  },
  [BookingStatus.CANCELED]: {
    labelKey: "bookings.whatsapp.cancelLabel",
    messageKey: "bookings.whatsapp.cancelMessage",
    bg: colorSurfaceError, color: colorActionDanger, border: colorBorderError,
    hoverBg: colorSurfaceDangerHover, hoverBorder: colorActionDanger,
  },
};

const AVATAR_BG = [
  iconBgInfo,
  iconBgSuccess,
  iconBgPurple,
  colorDealBg,
  iconBgPink,
];
const AVATAR_FG = [
  iconColorInfo,
  iconColorSuccess,
  iconColorPurple,
  colorDealAccent,
  iconColorPink,
];

const getAvatarStyle = (name: string) => {
  const idx =
    (name.charCodeAt(0) + (name.charCodeAt(1) ?? 0)) % AVATAR_BG.length;
  return { bg: AVATAR_BG[idx], color: AVATAR_FG[idx] };
};

const getInitials = (name: string) => {
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2)
    return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
  return name.slice(0, 2).toUpperCase();
};

interface Props {
  booking: Booking;
  onStatusChange: (booking: Booking, status: BookingStatus) => void;
  isBookingUpdateEnabled: boolean;
  restaurantName: string;
}

export const BookingListItem = ({
  booking,
  onStatusChange,
  isBookingUpdateEnabled,
  restaurantName,
}: Props) => {
  const { t } = useTranslation();
  const [detailsOpen, setDetailsOpen] = useState(false);
  const avatarStyle = getAvatarStyle(booking.userName);
  const initials = getInitials(booking.userName);
  const allowedTransitions = getAllowedBookingTransitions(booking.bookingStatus);
  const whatsappAction = WHATSAPP_ACTIONS[booking.bookingStatus];

  // Opens WhatsApp with a prefilled message built from the booking. The
  // message key selects which notification (confirmation or cancellation) is
  // sent; both share the same booking placeholders.
  const openWhatsapp = (messageKey: string) => {
    const message = t(messageKey, {
      guest: booking.userName,
      restaurant: restaurantName,
      date: dayjs(booking.bookingDate).format("dddd D MMMM YYYY"),
      time: booking.bookingHour,
      seats: booking.numberOfSeats,
    });
    window.open(
      buildWhatsappUrl(booking.phone, message),
      "_blank",
      "noopener,noreferrer",
    );
  };

  return (
    <>
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          gap: 2,
          py: 2,
          px: 2.5,
          borderBottom: `1px solid ${colorBorder}`,
          "&:last-child": { borderBottom: "none" },
          "&:hover": { backgroundColor: colorSurfaceHover },
        }}
      >
        <Avatar
          sx={{
            width: 40,
            height: 40,
            backgroundColor: avatarStyle.bg,
            color: avatarStyle.color,
            fontSize: "13px",
            fontWeight: 700,
            flexShrink: 0,
            mt: 0.25,
          }}
        >
          {initials}
        </Avatar>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              mb: 0.5,
              flexWrap: "wrap",
            }}
          >
            <Typography
              sx={{
                fontWeight: 600,
                fontSize: "14px",
                color: colorTextPrimary,
              }}
            >
              {booking.userName}
            </Typography>
            <BookingStatusBadge status={booking.bookingStatus} />
            {booking.hasNoShowWarning && (
              <NoShowWarningIndicator count={booking.noShowCount} />
            )}
          </Box>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              flexWrap: "wrap",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <AccessTimeOutlinedIcon
                sx={{ fontSize: 13, color: colorTextMuted }}
              />
              <Typography sx={{ fontSize: "13px", color: colorTextMuted }}>
                {booking.bookingHour}
              </Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <PeopleOutlineIcon sx={{ fontSize: 13, color: colorTextMuted }} />
              <Typography sx={{ fontSize: "13px", color: colorTextMuted }}>
                {booking.numberOfSeats} {t("bookings.list.guests")}
              </Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <PhoneOutlinedIcon sx={{ fontSize: 13, color: colorTextMuted }} />
              <Typography sx={{ fontSize: "13px", color: colorTextMuted }}>
                {booking.phone}
              </Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <TableRestaurantOutlinedIcon
                sx={{ fontSize: 13, color: colorTextMuted }}
              />
              <Typography sx={{ fontSize: "13px", color: colorTextMuted }}>
                {booking.assignedTable === null
                  ? t("bookings.list.unassignedTable")
                  : t("bookings.list.table", { number: booking.assignedTable })}
              </Typography>
            </Box>
          </Box>

          {(booking.dealContent || booking.notes) && (
            <Box
              sx={{ mt: 0.75, display: "flex", gap: 0.75, flexWrap: "wrap" }}
            >
              {booking.dealContent && (
                <Chip
                  label={booking.dealContent}
                  size="small"
                  sx={{
                    fontSize: "12px",
                    backgroundColor: colorDealBg,
                    color: colorDealText,
                    border: `1px solid ${colorDealBorder}`,
                    height: "20px",
                    borderRadius: "6px",
                  }}
                />
              )}
              {booking.notes && (
                <Chip
                  label={booking.notes}
                  size="small"
                  sx={{
                    fontSize: "12px",
                    backgroundColor: colorChipBackground,
                    color: colorTextSecondary,
                    border: `1px solid ${colorBorder}`,
                    height: "20px",
                    borderRadius: "6px",
                  }}
                />
              )}
            </Box>
          )}
        </Box>

        {isBookingUpdateEnabled && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.75,
              flexShrink: 0,
              mt: 0.25,
              flexWrap: "wrap",
              justifyContent: "flex-end",
            }}
          >
            <Button
              size="small"
              variant="outlined"
              onClick={() => setDetailsOpen(true)}
              sx={{
                textTransform: "none",
                fontSize: "12px",
                fontWeight: 500,
                borderColor: colorBorder,
                color: colorTextPrimary,
                borderRadius: "8px",
                px: 1.5,
                py: 0.5,
                "&:hover": {
                  borderColor: colorTextDisabled,
                  backgroundColor: colorSurfaceHover,
                },
              }}
            >
              {t("bookings.list.viewDetails")}
            </Button>

            {whatsappAction && (
              <Button
                size="small"
                variant="outlined"
                startIcon={<WhatsAppIcon sx={{ fontSize: 16 }} />}
                onClick={() => openWhatsapp(whatsappAction.messageKey)}
                sx={{
                  textTransform: "none",
                  fontSize: "12px",
                  fontWeight: 500,
                  borderColor: whatsappAction.border,
                  color: whatsappAction.color,
                  borderRadius: "8px",
                  px: 1.5,
                  py: 0.5,
                  backgroundColor: whatsappAction.bg,
                  "&:hover": {
                    borderColor: whatsappAction.hoverBorder,
                    backgroundColor: whatsappAction.hoverBg,
                  },
                }}
              >
                {t(whatsappAction.labelKey)}
              </Button>
            )}

            {allowedTransitions.map(({ event, target }) => {
              const style = EVENT_ACTIONS[event];
              return (
                <Button
                  key={event}
                  size="small"
                  variant="outlined"
                  onClick={() => onStatusChange(booking, target)}
                  sx={{
                    textTransform: "none",
                    fontSize: "12px",
                    fontWeight: 500,
                    borderColor: style.border,
                    color: style.color,
                    borderRadius: "8px",
                    px: 1.5,
                    py: 0.5,
                    backgroundColor: style.bg,
                    "&:hover": {
                      borderColor: style.hoverBorder,
                      backgroundColor: style.hoverBg,
                    },
                  }}
                >
                  {t(style.labelKey)}
                </Button>
              );
            })}
          </Box>
        )}
      </Box>

      <BookingDetailsModal
        booking={booking}
        open={detailsOpen}
        onClose={() => setDetailsOpen(false)}
      />
    </>
  );
};
